// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { formatCurrency } from '@/utilities/number';
import { Tooltip, Typography } from '@mui/material';

const ESTIMATE_TOOLTIP =
  'Estimated value based on planned quantity and the item default cost basis (will update to actual value of serialized assets once an inbound shipment is received or an outbound shipment is shipped)';

interface ShipmentValueDisplayProps {
  // The locked actual value, present once the shipment ships or is received
  actualValue: string | null;
  // The frozen estimate captured at setup, shown before the shipment locks
  estimatedValue: string | null;
  // Typography variant for the rendered figure; footers use a bolder variant
  variant?: 'body2' | 'subtitle2';
}

/**
 * Renders a shipment value: the locked actual value once present, otherwise the
 * frozen estimate as a tentative figure (secondary gray, italic, dotted
 * underline, with an explanatory tooltip), and a blank placeholder when neither
 * exists.
 */
export function ShipmentValueDisplay(props: ShipmentValueDisplayProps) {
  const variant = props.variant ?? 'body2';

  if (props.actualValue !== null) {
    return (
      <Typography variant={variant}>
        {formatCurrency(props.actualValue)}
      </Typography>
    );
  }

  if (props.estimatedValue !== null) {
    return (
      <Tooltip arrow title={ESTIMATE_TOOLTIP}>
        <Typography
          variant={variant}
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
          }}
        >
          {formatCurrency(props.estimatedValue)}
        </Typography>
      </Tooltip>
    );
  }

  return <Typography variant={variant}>{formatCurrency(null)}</Typography>;
}
